import { loadStripe } from "@stripe/stripe-js";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

import stripeConfig, { resolveStripePublishableKey } from "@/config/stripe.config";
import PaymentProfileContext from "@/context/PaymentProfileContext";
import ProfileDataContext from "@/context/ProfileDataContext";
import useOverlayHistory from "@/hooks/useOverlayHistory";
import paymentProfileService from "@/services/paymentProfile.service";
import PaymentProfileModal from "@/shared-components/payment-profile/PaymentProfileModal";
import { resolvePaymentCountryCode } from "@/utils/paymentCountry.utils";

function coerceStatusFromProfile(userSession, candidateProfile = null) {
  if (!userSession) {
    return "unauthenticated";
  }

  const typeCandidate = userSession?.user_type ?? userSession?.__t ?? null;
  const normalizedType = typeof typeCandidate === "string" ? typeCandidate.trim().toLowerCase() : "";
  if (normalizedType === "gallery") {
    return "unsupported";
  }

  const profile = candidateProfile ?? userSession?.auction_bidder_profile ?? userSession?.bidder_profile ?? null;
  const status = profile?.status ?? null;

  if (status === "verified" || status === "authorized") {
    return "verified";
  }

  if (status === "pending_action" || status === "requires_payment_method" || status === "requires_action") {
    return "pending";
  }

  if (userSession?.user_payment?.stripe_customer_id) {
    return "pending";
  }

  return "missing";
}

const initialModalState = Object.freeze({
  open: false,
  mode: null,
  authorization: null,
  defaultCountry: null,
  pendingBid: null,
  resolve: null,
  reject: null,
});

function deriveCountryHintFromAuthorization(payload, fallback = null) {
  if (!payload || typeof payload !== "object") {
    return fallback;
  }

  const metadata = payload.metadata ?? {};
  const candidates = [
    payload.country_hint,
    payload.countryHint,
    metadata.auction_bid_country,
    metadata.country,
    payload.country,
    payload.billing_country,
  ];

  for (const candidate of candidates) {
    if (typeof candidate === "string" && candidate.trim()) {
      const countryCode = resolvePaymentCountryCode(candidate);
      if (countryCode) {
        return countryCode;
      }
    }
  }

  return resolvePaymentCountryCode(fallback) ?? fallback;
}

function extractPendingBid(payload) {
  if (!payload || typeof payload !== "object") {
    return null;
  }

  const candidate = payload.pending_bid ?? payload.pendingBid ?? null;
  if (candidate && typeof candidate === "object") {
    return candidate;
  }
  return null;
}

function mapStripeStatus(stripeStatus) {
  switch (stripeStatus) {
  case "requires_payment_method":
    return "requires_payment_method";
  case "requires_action":
  case "requires_confirmation":
    return "requires_action";
  case "processing":
  case "requires_capture":
    return "awaiting_capture";
  case "succeeded":
    return "succeeded";
  case "canceled":
    return "cancelled";
  default:
    return "pending_authorization";
  }
}

function shouldAutoConfirmAuthorization(payload) {
  if (!payload || typeof payload !== "object") {
    return false;
  }

  const clientSecret = typeof payload.stripe_client_secret === "string"
    ? payload.stripe_client_secret.trim()
    : "";
  if (!clientSecret) {
    return false;
  }

  const status = typeof payload.status === "string"
    ? payload.status.trim().toLowerCase()
    : "";

  return payload.requires_action === true || status === "requires_action" || status === "requires_confirmation";
}

function derivePaymentMethodIdFromAuthorization(payload, profile = null) {
  const profileSnapshot = profile?.auction_bidder_profile ?? profile ?? {};
  const candidates = [
    payload?.stripe_payment_method_id,
    payload?.stripe_payment_method,
    payload?.payment_method,
    profileSnapshot?.stripe_payment_method_id,
    profileSnapshot?.payment_method_id,
  ];

  for (const candidate of candidates) {
    if (typeof candidate === "string" && candidate.trim()) {
      return candidate.trim();
    }
  }
  return null;
}

function buildAuthorizationFromStripeIntent({ authorization, intent }) {
  if (!authorization || !intent) {
    return authorization ?? null;
  }

  const updated = {
    ...authorization,
    status: mapStripeStatus(intent.status),
    stripe_status: intent.status ?? authorization?.stripe_status ?? null,
    stripe_client_secret: intent.client_secret ?? authorization?.stripe_client_secret ?? null,
    stripe_payment_intent_id: intent.id ?? authorization?.stripe_payment_intent_id ?? null,
    stripe_payment_method_id: intent.payment_method ?? authorization?.stripe_payment_method_id ?? null,
    requires_action: false,
    requires_payment_method: false,
  };

  if (updated.pending_bid) {
    delete updated.pending_bid;
  }
  if (updated.stripe_next_action) {
    delete updated.stripe_next_action;
  }

  return updated;
}

export default function PaymentProfileProvider({ children }) {
  const { profileData, setProfileData } = useContext(ProfileDataContext);
  const [profile, setProfile] = useState(() => profileData?.auction_bidder_profile ?? null);
  const [status, setStatus] = useState(() => coerceStatusFromProfile(profileData));
  const [modalState, setModalState] = useState(initialModalState);
  const [stripePromise, setStripePromise] = useState(null);

  const resolveStripeInstance = useCallback(async () => {
    if (stripePromise) {
      return stripePromise;
    }
    const key = stripeConfig.publishableKey || await resolveStripePublishableKey();
    if (!key) {
      return null;
    }
    const promise = loadStripe(key);
    setStripePromise(promise);
    return promise;
  }, [stripePromise]);

  const latestProfileRef = useRef(profile);

  const fallbackCardholderName = useMemo(() => {
    if (!profileData) {
      return "Cardholder";
    }
    const candidates = [
      profileData?.user_name,
      profileData?.user_username,
      profileData?.display_name,
      profileData?.name,
      profileData?.first_name,
      profileData?.last_name,
      profileData?.user_profile_info?.user_name,
      profileData?.user_profile_info?.display_name,
    ];
    for (const candidate of candidates) {
      if (typeof candidate === "string" && candidate.trim()) {
        return candidate.trim();
      }
    }
    return "Cardholder";
  }, [profileData]);

  useEffect(() => {
    const nextProfile = profileData?.auction_bidder_profile ?? null;
    latestProfileRef.current = nextProfile;
    setProfile(nextProfile);
    setStatus(coerceStatusFromProfile(profileData, nextProfile));
  }, [profileData]);

  const updateProfileSnapshot = useCallback((nextProfileSnapshot) => {
    latestProfileRef.current = nextProfileSnapshot;
    setProfile(nextProfileSnapshot);
    setStatus(coerceStatusFromProfile(profileData, nextProfileSnapshot));

    if (typeof setProfileData === "function" && profileData) {
      setProfileData({
        ...profileData,
        auction_bidder_profile: nextProfileSnapshot,
      });
    }
  }, [profileData, setProfileData]);

  const refreshProfile = useCallback(async () => {
    if (!profileData || status === "unsupported") {
      return null;
    }

    try {
      const { response, data } = await paymentProfileService.getProfile();
      if (response?.ok) {
        updateProfileSnapshot(data);
        return data;
      }
    }
    catch (error) {
      console.warn("[PaymentProfile] Failed to refresh profile", error);
    }

    return null;
  }, [profileData, status, updateProfileSnapshot]);

  const saveBillingDetails = useCallback(async ({
    billingAddress,
    billingInfo,
    country,
  } = {}) => {
    const { response, data } = await paymentProfileService.updateProfile({
      billingAddress,
      billingInfo,
      country,
    });

    if (!response?.ok) {
      const message = data?.error_message ?? "Unable to save billing details.";
      const error = new Error(message);
      error.response = response;
      error.data = data;
      throw error;
    }

    const nextProfile = data?.profile ?? data ?? null;
    if (nextProfile) {
      updateProfileSnapshot(nextProfile);
    }

    return nextProfile;
  }, [updateProfileSnapshot]);

  const closeModal = useCallback(() => {
    setModalState(initialModalState);
  }, []);

  const cancelModal = useCallback((reason) => {
    if (reason instanceof Error) {
      modalState.reject?.(reason);
    }
    else {
      const error = new Error("Bidder profile verification cancelled");
      error.code = "BIDDER_PROFILE_CANCELLED";
      modalState.reject?.(error);
    }
    closeModal();
  }, [closeModal, modalState]);

  useOverlayHistory({ open: modalState.open, onClose: cancelModal });

  const ensurePaymentProfile = useCallback(async ({ countryHint, forceSetup = false, pendingBid = null } = {}) => {
    if (!profileData) {
      const error = new Error("Authentication required to create a payment profile.");
      error.code = "BIDDER_PROFILE_UNAUTHENTICATED";
      throw error;
    }

    if (status === "unsupported") {
      const error = new Error("Gallery accounts cannot create payment profiles.");
      error.code = "BIDDER_PROFILE_UNSUPPORTED";
      throw error;
    }

    if (status === "verified" && !forceSetup) {
      return { status: "verified", profile: latestProfileRef.current };
    }

    const stripe = await resolveStripeInstance();
    if (!stripe) {
      const error = new Error("Stripe publishable key is not configured.");
      error.code = "BIDDER_PROFILE_STRIPE_MISSING";
      throw error;
    }

    return new Promise((resolve, reject) => {
      setModalState({
        open: true,
        mode: "setup",
        authorization: null,
        defaultCountry: countryHint ?? null,
        pendingBid: pendingBid ?? null,
        resolve: (result) => {
          resolve(result);
          closeModal();
        },
        reject: (reason) => {
          reject(reason);
          closeModal();
        },
      });
    });
  }, [closeModal, profileData, status, resolveStripeInstance]);

  const confirmAuthorizationWithStoredCard = useCallback(async ({ authorization, pendingBid = false } = {}) => {
    if (!authorization || typeof authorization !== "object") {
      return null;
    }

    const clientSecretRaw = authorization.stripe_client_secret ?? authorization.client_secret ?? null;
    const clientSecret = typeof clientSecretRaw === "string" ? clientSecretRaw.trim() : "";
    if (!clientSecret) {
      return null;
    }

    const stripe = await resolveStripeInstance();
    if (!stripe) {
      const error = new Error("Stripe publishable key is not configured.");
      error.code = "BIDDER_PROFILE_STRIPE_MISSING";
      throw error;
    }

    const paymentMethodId = derivePaymentMethodIdFromAuthorization(authorization, latestProfileRef.current);
    const confirmParams = paymentMethodId ? { payment_method: paymentMethodId } : undefined;

    const result = await stripe.confirmCardPayment(clientSecret, confirmParams);
    if (result?.error) {
      throw result.error;
    }

    const intent = result?.paymentIntent ?? null;
    if (!intent) {
      return null;
    }

    const nextStatus = mapStripeStatus(intent.status);
    if (nextStatus === "requires_action" || nextStatus === "requires_payment_method") {
      return null;
    }

    const updatedAuthorization = buildAuthorizationFromStripeIntent({
      authorization,
      intent,
    });

    const updatedProfile = {
      ...(latestProfileRef.current ?? {}),
      status: "verified",
      last_authorization: {
        intent_id: intent.id ?? null,
        status: nextStatus,
        stripe_status: intent.status ?? null,
        payment_method_id: updatedAuthorization?.stripe_payment_method_id ?? paymentMethodId ?? null,
        acknowledged_at: new Date().toISOString(),
      },
    };

    updateProfileSnapshot(updatedProfile);

    const country = deriveCountryHintFromAuthorization(authorization, null);

    try {
      await paymentProfileService.acknowledgeAuthorization({
        intentId: intent.id,
        paymentMethodId: updatedAuthorization?.stripe_payment_method_id ?? paymentMethodId ?? null,
        country,
      });
    }
    catch (ackError) {
      console.warn("[PaymentProfile] Failed to acknowledge stored-card authorization", ackError);
    }

    const response = {
      status: updatedProfile?.status ?? "verified",
      profile: updatedProfile,
    };

    if (pendingBid) {
      response.pending_bid_authorization = updatedAuthorization;
    }

    return response;
  }, [resolveStripeInstance, updateProfileSnapshot]);

  const handleAuthorizationChallenge = useCallback(async (authorizationPayload = {}) => {
    if (!profileData) {
      return { status };
    }

    if (status === "unsupported") {
      return { status };
    }

    if (!authorizationPayload || typeof authorizationPayload !== "object") {
      return { status };
    }

    const requiresManualFlow =
      authorizationPayload.requires_payment_method === true ||
      authorizationPayload.requires_action === true ||
      authorizationPayload.status === "requires_payment_method" ||
      authorizationPayload.status === "requires_action";

    if (!requiresManualFlow) {
      if (authorizationPayload.status === "succeeded" || authorizationPayload.status === "awaiting_capture") {
        updateProfileSnapshot({
          ...(latestProfileRef.current ?? {}),
          status: "verified",
          last_authorization: authorizationPayload,
        });
      }
      return { status };
    }

    const pendingBid = extractPendingBid(authorizationPayload);

    if (shouldAutoConfirmAuthorization(authorizationPayload)) {
      try {
        const autoResult = await confirmAuthorizationWithStoredCard({
          authorization: authorizationPayload,
          pendingBid: Boolean(pendingBid),
        });
        if (autoResult) {
          return autoResult;
        }
      }
      catch (autoError) {
        const shouldBubbleError =
          autoError?.code === "BIDDER_PROFILE_STRIPE_MISSING" ||
          autoError?.type === "card_error" ||
          autoError?.code === "payment_intent_authentication_failure";
        if (shouldBubbleError) {
          throw autoError;
        }
        console.warn("[PaymentProfile] Stored-card authorization confirmation failed", autoError);
      }
    }
    if (pendingBid) {
      const countryHint = deriveCountryHintFromAuthorization(
        authorizationPayload,
        authorizationPayload.country_hint ?? null
      );
      try {
        const result = await ensurePaymentProfile({
          countryHint: countryHint ?? undefined,
          forceSetup: true,
          pendingBid,
        });
        return result ?? { status };
      }
      catch (error) {
        if (error?.code === "BIDDER_PROFILE_CANCELLED") {
          return { status };
        }
        throw error;
      }
    }

    const stripe = await resolveStripeInstance();
    if (!stripe) {
      const error = new Error("Stripe publishable key is not configured.");
      error.code = "BIDDER_PROFILE_STRIPE_MISSING";
      throw error;
    }

    return new Promise((resolve, reject) => {
      setModalState({
        open: true,
        mode: "authorization",
        authorization: authorizationPayload,
        defaultCountry: null,
        pendingBid: null,
        resolve: (result) => {
          resolve(result);
          closeModal();
        },
        reject: (reason) => {
          reject(reason);
          closeModal();
        },
      });
    });
  }, [closeModal, confirmAuthorizationWithStoredCard, ensurePaymentProfile, fallbackCardholderName, profileData, status, updateProfileSnapshot, resolveStripeInstance]);

  const handleModalSubmit = useCallback(async ({
    paymentMethodId,
    intent,
    country,
    mode,
    pendingBid,
    cardholderName,
  }) => {
    let resultingProfile = null;
    let pendingBidAuthorization = null;
    let pendingBidError = null;

    try {
      const { response, data } = await paymentProfileService.confirmProfile({
        paymentMethodId,
        intentId: intent?.id ?? null,
        country,
        mode,
        pendingBid,
        cardholderName,
      });

      if (response?.ok) {
        resultingProfile = data?.profile ?? data ?? null;
        pendingBidAuthorization = data?.pending_bid_authorization ?? null;
        pendingBidError = data?.pending_bid_error ?? null;
      }
      else if (response && response.status === 404) {
        resultingProfile = {
          ...(latestProfileRef.current ?? {}),
          status: "verified",
          country,
          payment_method_id: paymentMethodId ?? null,
          cardholder_name: cardholderName ?? fallbackCardholderName,
        };
      }
    }
    catch (error) {
      if (error?.response?.status === 404) {
        resultingProfile = {
          ...(latestProfileRef.current ?? {}),
          status: "verified",
          country,
          payment_method_id: paymentMethodId ?? null,
          cardholder_name: cardholderName ?? fallbackCardholderName,
        };
      }
      else {
        throw error;
      }
    }

    if (!resultingProfile) {
      resultingProfile = {
        ...(latestProfileRef.current ?? {}),
        status: "verified",
        country,
        payment_method_id: paymentMethodId ?? null,
        cardholder_name: cardholderName ?? fallbackCardholderName,
      };
    }

    updateProfileSnapshot(resultingProfile);

    try {
      if (intent?.id) {
        await paymentProfileService.acknowledgeAuthorization({
          intentId: intent.id,
          paymentMethodId: paymentMethodId ?? intent?.payment_method ?? null,
          country,
        });
      }
    }
    catch (ackError) {
      console.warn("[PaymentProfile] Failed to acknowledge authorization", ackError);
    }

    return {
      status: resultingProfile?.status ?? "verified",
      profile: resultingProfile,
      pending_bid_authorization: pendingBidAuthorization,
      pending_bid_error: pendingBidError,
    };
  }, [fallbackCardholderName, updateProfileSnapshot]);

  const prepareSetupIntent = useCallback(async ({ country }) => {
    const { response, data } = await paymentProfileService.createSetupIntent({ country });
    return { response, data };
  }, []);

  const contextValue = useMemo(() => ({
    status,
    profile,
    ensurePaymentProfile,
    handleAuthorizationChallenge,
    refreshProfile,
    saveBillingDetails,
  }), [ensurePaymentProfile, handleAuthorizationChallenge, profile, refreshProfile, saveBillingDetails, status]);

  return (
    <PaymentProfileContext.Provider value={contextValue}>
      {children}
      <PaymentProfileModal
        open={modalState.open}
        mode={modalState.mode}
        authorization={modalState.authorization}
        defaultCountry={modalState.defaultCountry}
        pendingBid={modalState.pendingBid}
        stripePromise={stripePromise}
        userEmail={profileData?.user_email ?? ""}
        userName={profileData?.user_name ?? profileData?.user_username ?? ""}
        initialProfile={profile ?? null}
        onPrepareSetupIntent={prepareSetupIntent}
        onSubmit={handleModalSubmit}
        onSuccess={(result) => {
          modalState.resolve?.(result);
          closeModal();
        }}
        onCancel={cancelModal}
      />
    </PaymentProfileContext.Provider>
  );
}
